// Helpers for loading the QuickSubmit embed that any host app can share. The
// host keeps its own session call and errors, since each host asks vets-api
// for a different kind of session.

// The version is a path segment the loader reads back without decoding, so
// anything that would need encoding is refused rather than encoded. It must
// start with a letter or digit, which also rules out "." and "..".
const BUNDLE_VERSION = /^[A-Za-z0-9][\w.+-]*$/;

// RegExp#test turns undefined into the string "undefined", which would pass.
export const isBundleVersion = value =>
  typeof value === 'string' && BUNDLE_VERSION.test(value);

// The loader reads its own version out of this path and refuses to redeem
// against any origin but its own, so both come from the session.
export const loaderUrlFor = ({ apiBase, bundleVersion }) =>
  `${new URL(apiBase).origin}/components/v1/${bundleVersion}/qs-embed.js`;

export class StaleLoaderError extends Error {}

const LOADER_TIMEOUT_MS = 30000;
// Keyed by tag, not URL: only one version can ever define the element.
const pending = new Map();
const failures = new Map();
const definedBy = new Map();

// Returns the host session expiry as epoch ms, or null when unknown or
// unparseable. Both SAML (http-date in GMT) and SiS (Date.toString() with a
// local-time offset) parse to the same instant; epoch ms has no timezone.
export const getHostSessionExpiration = () => {
  try {
    const value = localStorage.getItem('sessionExpiration');
    if (value == null) return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  } catch {
    return null;
  }
};

// Returns an onActivity() function the embed calls (at most every 10 s)
// whenever the user or an upload is active. Each call reads the host
// session expiry and decides whether to ping vets-api.
//
// ping:                 () => Promise — a signed-in vets-api call; the
//                       platform uses it to refresh the SiS token and
//                       update localStorage.sessionExpiration.
// thresholdMs:          ping when the expiry is within this many ms
//                       (default 5 min).
// missingKeyCooldownMs: when the key is absent, at most one ping per this
//                       many ms (default 60 s) to avoid a /user call every
//                       10 s on network error or local dev.
// onExpired:            called once if the expiry is already in the past.
export const createHostSessionExtender = ({
  ping,
  thresholdMs = 5 * 60 * 1000,
  missingKeyCooldownMs = 60 * 1000,
  onExpired,
}) => {
  let pingInFlight = false;
  let lastMissingKeyPingAt = -Infinity;
  let expiredFired = false;

  const firePing = () => {
    if (pingInFlight) return;
    pingInFlight = true;
    Promise.resolve()
      .then(ping)
      .catch(() => {})
      .finally(() => {
        pingInFlight = false;
      });
  };

  return function onActivity() {
    const expiry = getHostSessionExpiration();

    if (expiry == null) {
      const now = Date.now();
      if (!pingInFlight && now - lastMissingKeyPingAt >= missingKeyCooldownMs) {
        lastMissingKeyPingAt = now;
        firePing();
      }
      return;
    }

    const remaining = expiry - Date.now();

    if (remaining <= 0) {
      if (!expiredFired) {
        expiredFired = true;
        onExpired?.();
      }
      return;
    }

    if (remaining > thresholdMs) return;

    firePing();
  };
};

export const loadEmbedElement = (url, tag) => {
  if (customElements.get(tag)) {
    // An element cannot be redefined, so a newer loader needs a fresh page.
    const loadedUrl = definedBy.get(tag);
    return loadedUrl && loadedUrl !== url
      ? Promise.reject(new StaleLoaderError(url))
      : Promise.resolve();
  }
  const inFlight = pending.get(tag);
  if (inFlight && inFlight.url !== url) {
    // Another version is already loading. Whichever way it goes, ask again
    // once it settles: it either defined the element (so this one is stale)
    // or failed (so this one can load).
    const again = () => loadEmbedElement(url, tag);
    return inFlight.loaded.then(again, again);
  }
  if (!inFlight) {
    // The browser remembers a failed module fetch per URL and replays the
    // failure without asking again, so a retry needs a URL of its own.
    const tries = failures.get(url) ?? 0;
    const src = tries ? `${url}?retry=${tries}` : url;
    const script = document.createElement('script');
    let timer;
    let timedOut = false;
    const loaded = new Promise((resolve, reject) => {
      script.type = 'module';
      script.src = src;
      script.onerror = () => reject(new Error(`Could not load ${src}`));
      // A script that loads but never defines the element would otherwise
      // leave the spinner up forever.
      timer = setTimeout(() => {
        timedOut = true;
        reject(new Error(`${tag} was not defined in time`));
      }, LOADER_TIMEOUT_MS);
      customElements.whenDefined(tag).then(resolve);
      document.head.appendChild(script);
    }).then(
      () => {
        clearTimeout(timer);
        pending.delete(tag);
        definedBy.set(tag, url);
      },
      error => {
        clearTimeout(timer);
        script.remove();
        pending.delete(tag);
        failures.set(url, tries + 1);
        if (timedOut) {
          // A module that has started cannot be stopped, so a slow one can
          // still define the element later. Without this the stale check
          // would pass for any version on the next try.
          customElements.whenDefined(tag).then(() => {
            if (!definedBy.has(tag)) definedBy.set(tag, url);
          });
        }
        throw error;
      },
    );
    pending.set(tag, { url, loaded });
  }
  return pending.get(tag).loaded;
};
